import { DateTime } from 'luxon';

export type TargetDueUiState = 'met' | 'past_due' | 'future';

/** GraphQL often returns datetimes as ISO strings; normalize to an instant. */
function parseDueAtInstant(dueAt: Date | string): DateTime | null {
  const dt =
    typeof dueAt === 'string'
      ? DateTime.fromISO(dueAt)
      : DateTime.fromJSDate(dueAt);

  return dt.isValid ? dt : null;
}

/**
 * Visual state for a target due instant vs met/unmet flags (TS-34051).
 */
export function getTargetDueUiState(
  dueAt: Date | string | undefined,
  targetMet: boolean | null | undefined,
  nowMs: number
): TargetDueUiState {
  if (dueAt == null || dueAt === '') {
    return 'future';
  }
  const due = parseDueAtInstant(dueAt);
  if (!due) {
    return 'future';
  }
  if (targetMet === true) {
    return 'met';
  }
  if (due.toMillis() < nowMs) {
    return 'past_due';
  }
  return 'future';
}

/**
 * Primary label in channel TZ, no timezone suffix.
 * Today/Tomorrow: "by" only while the due instant is still in the future; once passed, "Today 4:35 PM".
 * Yesterday: "Yesterday h:mm a". Else: "EEE MMM d, yyyy - h:mm a".
 */
export function formatTargetDuePrimaryLabel(
  dueAt: Date | string,
  timeZone: string,
  nowMs: number
): string {
  const dueInstant = parseDueAtInstant(dueAt);
  if (!dueInstant) {
    return '';
  }

  const due = dueInstant.setZone(timeZone);
  const now = DateTime.fromMillis(nowMs).setZone(timeZone);

  if (!due.isValid) {
    return '';
  }

  const timeStr = due.toFormat('h:mm a');
  const dueStillFuture = dueInstant.toMillis() >= nowMs;

  if (due.hasSame(now, 'day')) {
    return dueStillFuture ? `Today by ${timeStr}` : `Today ${timeStr}`;
  }
  if (due.hasSame(now.plus({ days: 1 }), 'day')) {
    return dueStillFuture ? `Tomorrow by ${timeStr}` : `Tomorrow ${timeStr}`;
  }
  if (due.hasSame(now.minus({ days: 1 }), 'day')) {
    return `Yesterday ${timeStr}`;
  }

  return due.toFormat("EEE MMM d, yyyy' - 'h:mm a");
}

export function withTargetPrefix(label: string): string {
  return `Target: ${label}`;
}
